import './App.css';
import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col } from '@op/opds.opux';
import { Office, AutoSuggestOption, OfficeBranchDetails } from './utils/types';
import { SearchForm } from './pages/SearchForm';
import { SearchDetailsView } from './pages/SearchDetailsView';
import { fetchOfficeApi } from './api/api';
import { mockApi } from './mockService/mockApi';
import { AppContext } from './context/AppContext';
import { useSearchParams } from 'react-router-dom';
import { getCurrentView } from './utils/navigation';
import appConfig from './config/config.json';
import { useTranslation } from 'react-i18next';
import { mapLanguageToLocale } from './utils/i18n';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * The main application component for the Office Search frontend.
 *
 * @remarks
 * This component sets up the global application state and context using React hooks and the `AppContext` provider.
 * The component renders the main search form and details view using React Router's `Routes`.
 *
 * @component
 *
 * @see {@link AppContext} - Provides global state and API access to descendant components.
 */

function App() {
  const { t, i18n } = useTranslation();
  const [autoSuggestOptions, setAutoSuggestOptions] = useState<
    AutoSuggestOption[]
  >([]);
  const [officeResults, setOfficeResults] = useState<Office[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [shouldFetchAutosuggest, setShouldFetchAutosuggest] = useState(true);
  const [showDetailView, setShowDetailView] = useState(false);
  const [officeDetails, setOfficeDetails] = useState<OfficeBranchDetails>(
    {} as OfficeBranchDetails
  );
  const [searchParams] = useSearchParams();
  const [currentLanguage, setCurrentLanguage] = useState(
    mapLanguageToLocale(i18n.language)
  );

  // Determine current view based on query parameters
  const currentView = getCurrentView(searchParams);

  // Track language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(mapLanguageToLocale(lng));
    };

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    // Set initial language
    setCurrentLanguage(mapLanguageToLocale(i18n.language));

    // Cleanup listener on unmount
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, t]);

  useEffect(() => {
    setError(''); // Clear error on view change
  }, [currentView]);

  const contextValue = useMemo(
    () => ({
      autoSuggestOptions,
      setAutoSuggestOptions,
      officeResults,
      setOfficeResults,
      loading,
      setLoading,
      error,
      setError,
      query,
      setQuery,
      shouldFetchAutosuggest,
      setShouldFetchAutosuggest,
      showDetailView,
      setShowDetailView,
      officeDetails,
      setOfficeDetails,
      api: appConfig.useMock ? mockApi : fetchOfficeApi,
      currentLanguage
    }),
    [
      autoSuggestOptions,
      officeResults,
      loading,
      error,
      query,
      shouldFetchAutosuggest,
      showDetailView,
      officeDetails,
      appConfig.useMock,
      currentLanguage
    ]
  );

  return (
    <ErrorBoundary>
      <AppContext.Provider value={contextValue}>
        <div className="op-search-form" style={{ marginTop: 0, paddingTop: 0 }}>
          <Row style={{ marginTop: 0, paddingTop: 0 }}>
            <Col
              xs={12}
              sm={12}
              lg={12}
              style={{ marginTop: 0, paddingTop: 0 }}
            >
              {currentView === 'details' ? (
                <SearchDetailsView />
              ) : (
                <SearchForm />
              )}
            </Col>
          </Row>
        </div>
      </AppContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
